import { motion } from "framer-motion";
import { useState } from "react";
import { X } from "lucide-react";

// Real work — SEVEN7H portfolio.
const IMAGES = [
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/gsnqlis4_ford-mustang-gt-black-car-wallpaper-3000x2000_42%20%281%29.png",
    label: "Ford Mustang GT · Full Detail",
    span: "md:col-span-8 aspect-[16/9]",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/tp2h3s6p_download%20%282%29%20%281%29.png",
    label: "Lamborghini Aventador SV · Paint Correction",
    span: "md:col-span-4 aspect-[4/5]",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/06g1z75a_472356094_517145968043065_4802531661848396451_n.webp",
    label: "Mitsubishi Lancer · Full Exterior Detail",
    span: "md:col-span-4 aspect-[3/4]",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/3tyb0ov5_476797891_546353745122287_8551755043379099609_n.webp",
    label: "Toyota Land Cruiser · Ceramic Coating",
    span: "md:col-span-8 aspect-[16/10]",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/2ijhqyzo_0f2a31_ef156fa4e2174af1a2b621bcd1eaae0a~mv2.webp",
    label: "Ford Ranger Raptor · Wash & Wax Full Detail",
    span: "md:col-span-4 aspect-[3/4]",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/x47y4ckh_0f2a31_6633eab13d264969b127237caa94c55b~mv2.webp",
    label: "Mercedes AMG C63 · V8 Biturbo Full Detail",
    span: "md:col-span-8 aspect-[4/3]",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/80b5yyg6_0f2a31_1498aa11afb1460e977b6b306227c905~mv2.webp",
    label: "Ford Falcon G6E · Full Detail & Ceramic Coating",
    span: "md:col-span-6 aspect-[4/5]",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_seven7h-detail/artifacts/dah13j6d_0f2a31_b63ffe0aec4745d59625dd412c297b00~mv2.webp",
    label: "Ford Mustang GT · Racing Stripes",
    span: "md:col-span-6 aspect-[4/5]",
  },
];

export default function Gallery() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-28 md:py-40 bg-[#050505] border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24"
        >
          <p className="md:col-span-5 eyebrow">Gallery — Previous Work</p>
          <h2 className="md:col-span-7 font-serif text-5xl md:text-7xl font-light tracking-tight text-white leading-[0.95]">
            Previous <span className="italic text-brand-red">work.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          {IMAGES.map((img, i) => (
            <motion.button
              key={img.src}
              type="button"
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightbox(img)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden bg-[#0a0a0a] ${img.span}`}
            >
              <img
                src={img.src}
                alt={img.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
              <div className="absolute inset-0 border border-white/0 group-hover:border-white/30 transition-colors duration-500" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-left">
                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/90">
                  0{i + 1} · {img.label}
                </span>
                <span className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  +
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-2xl flex items-center justify-center p-6 md:p-16 cursor-zoom-out"
        >
          <button
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <motion.img
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            src={lightbox.src}
            alt={lightbox.label}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-6 font-mono text-[11px] tracking-[0.24em] uppercase text-white/70">
            {lightbox.label}
          </div>
        </motion.div>
      )}
    </section>
  );
}
